import { Holiday, HolidayCountry, HolidayType, ISODateString } from './models';

export interface HolidayProviderMetadata {
    id: string;
    country: HolidayCountry;
    sourceName: string;
    sourceUrls: string[];
    supportedYears: number[];
    fallback: boolean;
    updatedAt: ISODateString;
    note: string;
}

export interface HolidayProvider {
    readonly metadata: HolidayProviderMetadata;
    getHolidaysForRange(startDate: ISODateString, endDate: ISODateString): Holiday[];
    getHolidaysForDate(date: ISODateString): Holiday[];
}

const KR_HOLIDAY_SOURCE_URLS = [
    'https://www.data.go.kr/data/15012690/openapi.do',
    'https://astro.kasi.re.kr/life/post/almanac',
    'https://www.kasa.go.kr/prog/plcyBrf/brief/kor/sub01_01_04/view.do?plcyBrfNo=431'
];

const KR_HOLIDAY_SEED: Holiday[] = [
    { id: 'kr-2025-0101-new-year', date: '2025-01-01', name: '1월 1일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0127-temporary-holiday', date: '2025-01-27', name: '임시공휴일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0128-seollal-eve', date: '2025-01-28', name: '설날 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0129-seollal', date: '2025-01-29', name: '설날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0130-seollal-next', date: '2025-01-30', name: '설날 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0301-independence', date: '2025-03-01', name: '3·1절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0303-substitute-independence', date: '2025-03-03', name: '대체공휴일(3·1절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2025-0505-childrens-day', date: '2025-05-05', name: '어린이날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0505-buddha', date: '2025-05-05', name: '석가탄신일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0506-substitute-buddha', date: '2025-05-06', name: '대체공휴일(석가탄신일)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2025-0603-presidential-election', date: '2025-06-03', name: '임시공휴일(대통령선거)', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0606-memorial', date: '2025-06-06', name: '현충일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-0815-liberation', date: '2025-08-15', name: '광복절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1003-foundation', date: '2025-10-03', name: '개천절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1005-chuseok-eve', date: '2025-10-05', name: '추석 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1006-chuseok', date: '2025-10-06', name: '추석', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1007-chuseok-next', date: '2025-10-07', name: '추석 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1008-substitute-chuseok', date: '2025-10-08', name: '대체공휴일(추석)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2025-1009-hangul', date: '2025-10-09', name: '한글날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2025-1225-christmas', date: '2025-12-25', name: '성탄절', country: 'KR', type: 'public', isPublicHoliday: true },

    { id: 'kr-2026-0101-new-year', date: '2026-01-01', name: '1월 1일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0216-seollal-eve', date: '2026-02-16', name: '설날 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0217-seollal', date: '2026-02-17', name: '설날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0218-seollal-next', date: '2026-02-18', name: '설날 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0301-independence', date: '2026-03-01', name: '3·1절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0302-substitute-independence', date: '2026-03-02', name: '대체공휴일(3·1절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2026-0501-labor', date: '2026-05-01', name: '노동절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0505-childrens-day', date: '2026-05-05', name: '어린이날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0524-buddha', date: '2026-05-24', name: '석가탄신일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0525-substitute-buddha', date: '2026-05-25', name: '대체공휴일(석가탄신일)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2026-0603-local-election', date: '2026-06-03', name: '전국동시지방선거', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0606-memorial', date: '2026-06-06', name: '현충일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0717-constitution', date: '2026-07-17', name: '제헌절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0815-liberation', date: '2026-08-15', name: '광복절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0817-substitute-liberation', date: '2026-08-17', name: '대체공휴일(광복절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2026-0924-chuseok-eve', date: '2026-09-24', name: '추석 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0925-chuseok', date: '2026-09-25', name: '추석', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-0926-chuseok-next', date: '2026-09-26', name: '추석 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-1003-foundation', date: '2026-10-03', name: '개천절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-1005-substitute-foundation', date: '2026-10-05', name: '대체공휴일(개천절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2026-1009-hangul', date: '2026-10-09', name: '한글날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2026-1225-christmas', date: '2026-12-25', name: '성탄절', country: 'KR', type: 'public', isPublicHoliday: true },

    { id: 'kr-2027-0101-new-year', date: '2027-01-01', name: '1월 1일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0206-seollal-eve', date: '2027-02-06', name: '설날 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0207-seollal', date: '2027-02-07', name: '설날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0208-seollal-next', date: '2027-02-08', name: '설날 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0209-substitute-seollal', date: '2027-02-09', name: '대체공휴일(설날)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-0301-independence', date: '2027-03-01', name: '3·1절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0501-labor', date: '2027-05-01', name: '노동절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0503-substitute-labor', date: '2027-05-03', name: '대체공휴일(노동절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-0505-childrens-day', date: '2027-05-05', name: '어린이날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0513-buddha', date: '2027-05-13', name: '석가탄신일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0606-memorial', date: '2027-06-06', name: '현충일', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0717-constitution', date: '2027-07-17', name: '제헌절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0719-substitute-constitution', date: '2027-07-19', name: '대체공휴일(제헌절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-0815-liberation', date: '2027-08-15', name: '광복절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0816-substitute-liberation', date: '2027-08-16', name: '대체공휴일(광복절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-0914-chuseok-eve', date: '2027-09-14', name: '추석 전날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0915-chuseok', date: '2027-09-15', name: '추석', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-0916-chuseok-next', date: '2027-09-16', name: '추석 다음 날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-1003-foundation', date: '2027-10-03', name: '개천절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-1004-substitute-foundation', date: '2027-10-04', name: '대체공휴일(개천절)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-1009-hangul', date: '2027-10-09', name: '한글날', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-1011-substitute-hangul', date: '2027-10-11', name: '대체공휴일(한글날)', country: 'KR', type: 'substitute', isPublicHoliday: true },
    { id: 'kr-2027-1225-christmas', date: '2027-12-25', name: '성탄절', country: 'KR', type: 'public', isPublicHoliday: true },
    { id: 'kr-2027-1227-substitute-christmas', date: '2027-12-27', name: '대체공휴일(성탄절)', country: 'KR', type: 'substitute', isPublicHoliday: true }
];

function supportedYearsFrom(seed: Holiday[]) {
    return Array.from(new Set(seed.map((holiday) => Number(holiday.date.slice(0, 4))))).sort((a, b) => a - b);
}

function sortHolidays(holidays: Holiday[]) {
    return [...holidays].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        if (a.type !== b.type) return holidayTypeRank(a.type) - holidayTypeRank(b.type);
        return a.name.localeCompare(b.name);
    });
}

function holidayTypeRank(type: HolidayType) {
    if (type === 'public') return 0;
    if (type === 'substitute') return 1;
    return 2;
}

export class LocalKoreanHolidayProvider implements HolidayProvider {
    private readonly holidays = sortHolidays(KR_HOLIDAY_SEED);
    private readonly holidaysByDate = this.holidays.reduce<Record<string, Holiday[]>>((acc, holiday) => {
        acc[holiday.date] = [...(acc[holiday.date] || []), holiday];
        return acc;
    }, {});

    public readonly metadata: HolidayProviderMetadata = {
        id: 'kr-local-seed',
        country: 'KR',
        sourceName: 'Korean public holiday local seed',
        sourceUrls: KR_HOLIDAY_SOURCE_URLS,
        supportedYears: supportedYearsFrom(KR_HOLIDAY_SEED),
        fallback: true,
        updatedAt: '2026-07-01',
        note: 'Client-side local fallback. Replace this provider with a server/API adapter when a data.go.kr service key can be stored outside the browser.'
    };

    public getHolidaysForRange(startDate: ISODateString, endDate: ISODateString): Holiday[] {
        return this.holidays.filter((holiday) => holiday.date >= startDate && holiday.date <= endDate);
    }

    public getHolidaysForDate(date: ISODateString): Holiday[] {
        return this.holidaysByDate[date] || [];
    }
}

export class HolidayService {
    constructor(private readonly provider: HolidayProvider = new LocalKoreanHolidayProvider()) { }

    public get metadata() {
        return this.provider.metadata;
    }

    public getHolidaysForRange(startDate: ISODateString, endDate: ISODateString): Holiday[] {
        return this.provider.getHolidaysForRange(startDate, endDate);
    }

    public getHolidaysForDate(date: ISODateString): Holiday[] {
        return this.provider.getHolidaysForDate(date);
    }

    public groupByDate(holidays: Holiday[]): Record<string, Holiday[]> {
        return holidays.reduce<Record<string, Holiday[]>>((acc, holiday) => {
            acc[holiday.date] = [...(acc[holiday.date] || []), holiday];
            return acc;
        }, {});
    }
}
